import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { CartActions, CartLineSources } from '@next/core-lib/cart';
import { DialogService } from '@next/core-lib/dialog';

import { I18nSelectors, LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';

import { UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, finalize, map, startWith, switchMap, withLatestFrom } from 'rxjs/operators';

import { SiloApiService } from 'src/app/services/silo-api.service';

import * as ProductActions from './actions';
import * as ProductSelectors from './selectors';
import { ElasticApiService } from '../../services/elastic-api.service';
import { FavoritesDialogComponent } from '../components/favorites/components/favorites-dialog/favorites-dialog.component';
import { ProductSearchDialogComponent } from '../components/product-search-dialog/product-search-dialog.component';
import { ProductWizardDialogComponent } from '../components/product-wizard-dialog/product-wizard-dialog.component';
import { ProductlistWizardDialogComponent } from '../components/productlist-wizard-dialog/productlist-wizard-dialog.component';
import { ReturnPackagingSearchDialogComponent } from '../components/return-packaging-search-dialog/return-packaging-search-dialog.component';
import { ReturnProductSearchDialogComponent } from '../components/return-product-search-dialog/return-product-search-dialog.component';
import { ProductWizardMode } from '../models/product.models';
import { CartApiService } from '../services/cart-api.service';
import { FavoriteApiService } from '../services/favorite-api.service';
import { ProductDeliveryApiService } from '../services/product-delivery-api.service';
import { ProductDeliveryMomentApiService } from '../services/product-delivery-moment-api.service';

@Injectable()
export class ProductEffects {
  searchRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.skuCatalogRequest),
      withLatestFrom(this.store.select(I18nSelectors.selectLanguage), this.store.select(UserSelectors.selectLocation)),
      filter(([_, language, location]) => !!language && !!location),
      switchMap(([{ keywords }, language, location]) =>
        this.elasticApiService
          .getSkuCatalog({
            keywords,
            language: language + '',
            salesOrganization: location.salesAreas?.[0]?.salesOrganization,
          })
          .pipe(
            map((skuCatalogItems) => ProductActions.skuCatalogSuccess({ skuCatalogItems })),
            catchError(() => of(ProductActions.skuCatalogFailure())),
          ),
      ),
    ),
  );

  addFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.addFavorite),
      withLatestFrom(this.store.select(UserSelectors.selectProfileId)),
      switchMap(([{ skuId }, profileId]) => this.favoriteApiService.addFavorite(profileId, skuId)),
      switchMap(() => of(ProductActions.updateFavoritesSuccess())),
    ),
  );

  removeFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.removeFavorite),
      withLatestFrom(this.store.select(UserSelectors.selectProfileId)),
      switchMap(([{ skuId }, profileId]) => this.favoriteApiService.removeFavorite(profileId, skuId)),
      map(() => ProductActions.updateFavoritesSuccess()),
    ),
  );

  updateFavoritesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.updateFavoritesSuccess),
      withLatestFrom(this.store.select(UserSelectors.selectProfileId)),
      map(([_, profileId]) => ProductActions.getFavorites({ profileId })),
    ),
  );

  getFavorites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.getFavorites),
      switchMap(({ profileId }) => this.favoriteApiService.getFavorites(profileId)),
      map((favoritesResponse) => ProductActions.getFavoritesSuccess({ skus: favoritesResponse.favorites })),
    ),
  );

  searchFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.skuCatalogFailure),
      map((_) =>
        NotificationActions.warningSnackbar({
          message: this.localeService.translate('product.search.search-failed'),
        }),
      ),
    ),
  );

  openSearchDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.openSearchDialog),
      switchMap(({ cart, searchTerm, htmlButtonRef, silo, source }) =>
        this.dialogService.fullscreenDialog(
          ProductSearchDialogComponent,
          {
            title: 'product.search.search-label',
            subTitle: 'product.search.search-sub-label',
            cancelButton: true,
            cancelButtonText: this.localeService.translate('product.search.back'),
            altButton: false,
            confirmButton: false,
            pushHistoryState: false,
            cart,
            searchTerm,
            htmlButtonRef,
            silo,
            source,
          },
          {
            autoFocus: false,
          },
        ),
      ),
      switchMap(() => of(ProductActions.resetSkuCatalog())),
    ),
  );

  openFavoritesDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.openFavoritesDialog),
      switchMap(({ cart, htmlButtonRef }) =>
        this.dialogService.fullscreenDialog(FavoritesDialogComponent, {
          title: this.localeService.translate('app.favorites.title'),
          subTitle: this.localeService.translate('app.favorites.description'),
          cancelButton: true,
          cancelButtonText: this.localeService.translate('app.favorites.cancel'),
          altButton: false,
          confirmButton: false,
          pushHistoryState: false,
          cart,
          htmlButtonRef,
        }),
      ),
      switchMap(() => of(ProductActions.resetFavoritesCatalog())),
    ),
  );

  openReturnSearchDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductActions.openReturnSearchDialog),
        switchMap(({ cart }) => {
          this.dialogService.fullscreenDialog(
            ReturnProductSearchDialogComponent,
            {
              title: 'product.return-search.dialog-title',
              cancelButton: true,
              cancelButtonText: this.localeService.translate('product.search.back'),
              altButton: false,
              confirmButton: false,
              pushHistoryState: false,
              cart,
            },
            {
              autoFocus: false,
            },
          );
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  openReturnPackagingSearchDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductActions.openReturnPackagingSearchDialog),
        switchMap(() => {
          this.dialogService.fullscreenDialog(
            ReturnPackagingSearchDialogComponent,
            {
              title: 'product.return-packaging-search.dialog-title',
              cancelButton: true,
              cancelButtonText: this.localeService.translate('product.search.back'),
              altButton: false,
              confirmButton: false,
              pushHistoryState: false,
            },
            {
              autoFocus: false,
            },
          );
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  openWizardDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.openWizardDialog),
      switchMap(({ skuCatalogItem, orderType, cartId, source }) =>
        this.dialogService.fullscreenDialog(
          ProductWizardDialogComponent,
          {
            title: 'product.wizard.quantity.tab-title',
            subTitle: skuCatalogItem.title,
            cancelButton: true,
            cancelButtonText: this.localeService.translate('product.wizard.back'),
            altButton: true,
            altButtonText: this.localeService.translate('product.wizard.next'),
            confirmButton: true,
            confirmButtonText: this.localeService.translate('product.add-to-cart'),
            isProductDialog: true,
            pushHistoryState: false,
            skuCatalogItem,
            mode: ProductWizardMode.ADD,
            disableCancelAfterDropdownClick: false,
            orderType,
            cartId,
            source: source,
          },
          {
            autoFocus: false,
          },
        ),
      ),
      switchMap(() =>
        of(ProductActions.resetDeliveryMoments(), ProductActions.resetSilos(), ProductActions.resetVariants(), ProductActions.resetSelectedCartLine()),
      ),
    ),
  );

  openWizardDialogForProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.openWizardDialogForProductList),
      switchMap(({ skuCatalogItem, silo }) =>
        this.dialogService.fullscreenDialog(
          ProductlistWizardDialogComponent,
          {
            title: 'product.wizard.quantity.tab-title',
            subTitle: skuCatalogItem.title,
            cancelButton: true,
            cancelButtonText: this.localeService.translate('product.wizard.back'),
            altButton: true,
            altButtonText: this.localeService.translate('product.wizard.next'),
            confirmButton: true,
            confirmButtonText: this.localeService.translate('product.add-to-silo'),
            pushHistoryState: false,
            skuCatalogItem,
            disableCancelAfterDropdownClick: false,
            silo,
            source: CartLineSources.SILO_PRODUCT_LIST,
          },
          {
            autoFocus: false,
          },
        ),
      ),
      switchMap(() => of(ProductActions.resetVariants())),
    ),
  );

  openWizardDialogForFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.openWizardDialogForFavorite),
      switchMap(({ favoriteSku, orderType, cartId }) =>
        this.dialogService.fullscreenDialog(
          ProductWizardDialogComponent,
          {
            title: 'product.wizard.quantity.tab-title',
            subTitle: favoriteSku.title,
            cancelButton: true,
            cancelButtonText: this.localeService.translate('product.wizard.back'),
            altButton: true,
            altButtonText: this.localeService.translate('product.wizard.next'),
            confirmButton: true,
            confirmButtonText: this.localeService.translate('product.add-to-cart'),
            isProductDialog: true,
            pushHistoryState: false,
            skuCatalogItem: {
              skuId: favoriteSku.id,
              title: favoriteSku.title,
              externalSkuId: favoriteSku.externalId,
              orderingInformation: favoriteSku.orderingInformation,
              quantity: favoriteSku.orderingInformation.minimumOrderUnitQuantity,
            },
            mode: ProductWizardMode.ADD,
            disableCancelAfterDropdownClick: false,
            orderType,
            cartId,
            source: CartLineSources.FAVORITES,
          },
          {
            autoFocus: false,
          },
        ),
      ),
      switchMap(() =>
        of(ProductActions.resetDeliveryMoments(), ProductActions.resetSilos(), ProductActions.resetVariants(), ProductActions.resetSelectedCartLine()),
      ),
    ),
  );

  openWizardDialogForExistingCartLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.openWizardDialogForExistingCartLine),
      switchMap(({ cartId, cartLine, initialSelectedTab, orderType }) =>
        this.dialogService.fullscreenDialog(
          ProductWizardDialogComponent,
          {
            title: 'product.wizard.quantity.tab-title',
            subTitle: cartLine.skuTitle,
            cancelButton: true,
            cancelButtonText: this.localeService.translate('product.wizard.back'),
            altButton: true,
            altButtonText: this.localeService.translate('product.wizard.next'),
            confirmButton: true,
            confirmButtonText: this.localeService.translate('product.save-in-cart'),
            isProductDialog: true,
            pushHistoryState: false,
            cartId,
            cartLine,
            initialSelectedTab,
            mode: ProductWizardMode.EDIT,
            disableCancelAfterDropdownClick: false,
            orderType,
            skuCatalogItem: {
              skuId: cartLine.skuId,
              title: cartLine.skuTitle,
              externalSkuId: cartLine.externalSkuId,
              orderingInformation: cartLine.orderingInformation,
            },
          },
          {
            autoFocus: false,
          },
        ),
      ),
      switchMap(() =>
        of(ProductActions.resetCartLineDeliveryMoments(), ProductActions.resetSilos(), ProductActions.resetVariants(), ProductActions.resetSelectedCartLine()),
      ),
    ),
  );

  getSilosRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.getSilosRequest),
      switchMap(() =>
        this.siloApiService.getSilos().pipe(
          map(({ silos }) => ProductActions.getSilosSuccess({ silos })),
          catchError((error: HttpErrorResponse) => of(NotificationActions.errorSnackbar({ message: error.message }), ProductActions.getSilosFailed())),
        ),
      ),
    ),
  );

  getSilosWithProductListRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.getSilosWithProductListRequest),
      switchMap(() => {
        const siloApiCallObservable = this.siloApiService.getProductListSilos().pipe(map(({ silos }) => silos));

        return siloApiCallObservable.pipe(
          map((silos) => ProductActions.getSilosWithProductListSuccess({ silos })),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message }), ProductActions.getSilosWithProductListFailed()),
          ),
        );
      }),
    ),
  );

  getSilosWithProductListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.getSilosWithProductListSuccess),
      map(({ silos }) => {
        const skuIds = silos.flatMap((silo) => silo.productList.map((product) => product.id));
        return ProductActions.getDeliveryProposalsRequest({ skuIds: [...new Set(skuIds)] });
      }),
    ),
  );

  addProductListRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.addProductToSiloProductListRequest),
      switchMap(({ request }) => {
        const apiCall$ = this.siloApiService.addProductListItem(request);
        return apiCall$.pipe(
          map((response) => ProductActions.addProductToSiloProductListSuccess({ response })),
          catchError((e: HttpErrorResponse) =>
            of(
              NotificationActions.errorSnackbar({ message: e.message }),
              ProductActions.addProductToSiloProductListFailed(),
              ProductActions.resetSelectedProductSiloLine(),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
            this.store.dispatch(ProductActions.resetSelectedProductSiloLine());
          }),
        );
      }),
    ),
  );

  getSkuDeliveryMomentsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.getSkuDeliveryMomentsRequest),
      switchMap(({ skuId, orderType, flexibility }) =>
        this.productDeliveryMomentApiService.getDeliveryMoments(skuId, orderType, flexibility).pipe(
          map((deliveryMomentsResponse) =>
            ProductActions.getDeliveryMomentsSuccess({
              deliveryMoments: deliveryMomentsResponse,
            }),
          ),

          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message }), ProductActions.getDeliveryMomentsFailed()),
          ),
        ),
      ),
    ),
  );

  getCartDeliveryMomentsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.getCartDeliveryDatesRequest),
      switchMap(({ cartId, orderType, flexibility }) =>
        this.cartApiService.getCartDeliveryMoments(cartId, orderType, flexibility).pipe(
          map((deliveryMomentsResponse) =>
            ProductActions.getDeliveryMomentsSuccess({
              deliveryMoments: deliveryMomentsResponse,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message }), ProductActions.getDeliveryMomentsFailed()),
          ),
        ),
      ),
    ),
  );

  patchCartDeliveryDateAndFlexibilityRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.patchCartDeliveryDateAndFlexibilityRequest),
      switchMap(({ cartId, deliveryDate, flexibility }) =>
        this.cartApiService.patchCartDeliveryDateAndFlexibility(cartId, deliveryDate, flexibility).pipe(
          map((cart) => CartActions.getCartSuccess({ cart })),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message }), ProductActions.getDeliveryMomentsFailed()),
          ),
        ),
      ),
    ),
  );

  getProductInformationsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.getProductInformationsRequest),
      withLatestFrom(this.store.select(I18nSelectors.selectLocale), this.store.select(UserSelectors.selectLocation)),
      filter(([_, locale, location]) => !!locale && !!location),
      switchMap(([{ skuIds }, locale, location]) =>
        this.elasticApiService.getSkuCatalogItemsByExternalSkuIds(skuIds.map(String), locale, location.salesAreas).pipe(
          map((productInformation) => ProductActions.getProductInformationsRequestSuccess({ productInformation })),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getDeliveryProposalsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.getDeliveryProposalsRequest),
      switchMap(({ skuIds }) => {
        if (skuIds.length === 0) {
          return of(ProductActions.getDeliveryProposalsRequestSuccess({ proposalDeliveryMoments: { deliveryDates: [], zeroFlexibilityDeliveryDates: [] } }));
        }

        return this.productDeliveryMomentApiService.getMultipleSkuDeliveryMoments(skuIds).pipe(
          map((deliveryMomentsResponse) => ProductActions.getDeliveryProposalsRequestSuccess({ proposalDeliveryMoments: deliveryMomentsResponse })),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  getVariantsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.getVariantsRequest),
      withLatestFrom(this.store.select(ProductSelectors.selectSelectedCartLine), this.store.select(ProductSelectors.selectSelectedProductSiloLine)),
      switchMap(([_, cartLine, siloLine]) => {
        const id = cartLine?.skuId || siloLine?.id;
        if (!id) {
          return of(NotificationActions.errorSnackbar({ message: 'No valid line selected' }), ProductActions.getVariantsFailed());
        }
        return this.productDeliveryApiService.getVariants(id).pipe(
          map(({ variants }) => {
            variants.sort((a, b) => a.description.localeCompare(b.description));
            return ProductActions.getVariantsSuccess({ variants });
          }),
          catchError((error: HttpErrorResponse) => of(NotificationActions.errorSnackbar({ message: error.message }), ProductActions.getVariantsFailed())),
        );
      }),
    ),
  );

  getCartLineDeliveryDatesInformation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.getCartLineDeliveryMomentsRequest),
      withLatestFrom(this.store.select(ProductSelectors.selectSelectedCartLine)),
      switchMap(([{ cartId, flexibility }, { id }]) =>
        this.cartApiService.getCartLineDeliveryMoments(cartId, id, flexibility).pipe(
          map((deliveryMomentsResponse) =>
            ProductActions.getCartLineDeliveryMomentsSuccess({
              deliveryMoments: deliveryMomentsResponse,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message }), ProductActions.getCartLineDeliveryMomentsFailed()),
          ),
        ),
      ),
    ),
  );

  getSilosInUseOnDatesRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.getSilosInUseOnDatesRequest),
      map(() =>
        ProductActions.getSilosInUseOnDatesSuccess({
          silosInUseOnDates: [],
        }),
      ),
      catchError((error: HttpErrorResponse) => of(NotificationActions.errorSnackbar({ message: error.message }), ProductActions.getSilosInUseOnDatesFailed())),
    ),
  );

  private readonly deliveryDateRetrievalTimeout = 5000;

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly dialogService: DialogService,
    private readonly elasticApiService: ElasticApiService,
    private readonly siloApiService: SiloApiService,
    private readonly productDeliveryMomentApiService: ProductDeliveryMomentApiService,
    private readonly productDeliveryApiService: ProductDeliveryApiService,
    private readonly cartApiService: CartApiService,
    private readonly favoriteApiService: FavoriteApiService,
  ) {}
}
